import { RadarDtg } from 'src/app/radar/radar.service';

import { NgClass } from '@angular/common';
import {
  Component,
  effect,
  inject,
  Injector,
  input,
  signal,
  untracked,
} from '@angular/core';

import {
  ControlComponent,
  LayerComponent,
  RasterSourceComponent,
} from 'ngx-mapbox-gl';

import { getRadarProjection } from './radar-map.utils';
import { resolveValidRadarTileUrl } from './radar-tile.utils';
import { RasterLayerSpecification } from 'mapbox-gl';

@Component({
  selector: 'app-radar-map-layers',
  templateUrl: './radar-map-layers.component.html',
  imports: [ControlComponent, RasterSourceComponent, LayerComponent, NgClass],
})
export class RadarMapLayersComponent {
  private injector = inject(Injector);

  dtgs = input<RadarDtg[]>([]);
  dtgIndex = input<number>(0);
  region = input<string>();
  showCellTracking = input<boolean>(false);
  radarPaintVisible = input<RasterLayerSpecification['paint']>();
  radarPaintInvisible = input<RasterLayerSpecification['paint']>();

  radarTileUrls = signal<Record<number, string | null>>({});

  constructor() {
    this.listenToRadarTileChanges();
  }

  getRadarProjection(region: string): [number, number, number, number] | null {
    return getRadarProjection(region);
  }

  private listenToRadarTileChanges(): void {
    effect(
      () => {
        const dtgs = this.dtgs();
        const region = this.region();
        const showCellTracking = this.showCellTracking();

        untracked(() => {
          this.resolveRadarTileUrls(dtgs, region, showCellTracking);
        });
      },
      { injector: this.injector }
    );
  }

  private async resolveRadarTileUrls(
    dtgs: RadarDtg[],
    region: string,
    showCellTracking: boolean
  ): Promise<void> {
    if (!dtgs?.length || !region) {
      this.radarTileUrls.set({});
      return;
    }

    const resolvedEntries = await Promise.all(
      dtgs.map(async (dtg, index) => {
        const url = await resolveValidRadarTileUrl(
          dtg,
          region,
          showCellTracking
        );

        return [index, url] as const;
      })
    );

    const urlMap = resolvedEntries.reduce<Record<number, string | null>>(
      (acc, [index, url]) => {
        acc[index] = url;
        return acc;
      },
      {}
    );

    this.radarTileUrls.set(urlMap);
  }
}
